/**
 * The 28px plate at the head of a phase row, carrying the mark of the party that OWNS that phase:
 * the warehouse extracted, RudderStack planned and transformed, the destination accepted or refused.
 *
 * Deliberately recessive: no stroke, no state colour at all. Run state is carried by the two LEGS of
 * the spine (`PhaseLeg`, `PhaseStage`), which keeps these reading as three nodes threaded on one
 * line rather than three status chips. It used to be carried by a dot immediately above each plate;
 * that dot is gone — three dots claimed three live positions and `activePhase()` can only ever
 * distinguish two.
 *
 * THE PLATE MASKS NOTHING. An earlier version of this comment said its "only job is masking the spine
 * running behind it"; there is no spine behind it. `PhaseStage`'s connector column stacks `spine-in`,
 * this plate and `spine-out` as SIBLINGS in a flex column, so nothing is ever drawn
 * underneath the mark — read `PhaseStage.tsx`'s "THE SPINE IS A FIXED GRID" note and its JSX, which say
 * so directly. What the plate actually does is OCCUPY the column: it takes vertical space between the
 * two segments, so the hairline breaks around it rather than through it.
 *
 * THE PLATE IS A FIXED LIGHT TILE (`bg-mark-plate`), NOT `bg-card` — and that is a deliberate reversal
 * of the previous note here, which said the `bg-card` fill was what made the break read as a gap in
 * one line rather than a chip laid over a rule. In LIGHT that claim still holds and nothing about it
 * has changed: `--mark-plate` is `#ffffff`, which is exactly what `--card` resolves to, so the PLATE
 * is pixel-identical to before — it still disappears into `RunOverview.tsx`'s `bg-card`
 * `run-phase-stack`. (The light ROW is not unchanged overall: the plan mark shrinks, per the optical
 * sizing note below. The plate is the part this paragraph is about.)
 *
 * In DARK the claim was the bug. `--card` there is near the surrounding surface, so "disappears into
 * the container" meant the vendor marks stood directly on a dark ground — and a vendor mark is
 * authored for a LIGHT one. Measured on this page at 1280x800: the Postgres elephant (dark navy, and a
 * raster fill inside its SVG, so it cannot be recoloured at all) was all but invisible, while the
 * RudderStack arc — `--brand-mark` is pinned `#ffffff` in dark — blazed.
 *
 * `--mark-plate` and its two companions are declared in `app.css` under `:root` ONLY and are
 * deliberately absent from the `.dark` block; in a value-flip theme that is how a colour says it does
 * not flip, and it is why this needs no banned `dark:` variant. The trade-off is written down beside
 * them: a light plate lowers raw contrast for a bright-brand mark (Snowflake 8.43:1 -> 2.37:1, measured
 * in the icon library's own "Back plate — proposal", Figma tz8HhvzK78lD3hAq263IgY node 29:2) while
 * rescuing every dark-ink one. The plate is `size-7` against a `size-4.5` mark — a 5px inset all round —
 * which is also what keeps the same file's other failure mode away: a full-bleed mark on a plate its
 * own size covers the plate completely and gains nothing.
 *
 * Brand marks stay at full colour and are never desaturated; "subtle" is achieved by size and by
 * removing the surrounding chrome, not by tinting the mark. (This rule was previously attributed to
 * "the icon set README" — no such file exists anywhere under `ui/`. It is a convention this surface
 * keeps, not a document it cites: `destinationTypes` and `warehouseTypes` both hand back the vendor
 * mark unmodified — in dark it stands on the round logo plate, a ground rather than a tint, which this
 * tile switches off with `logo-plate-off` because the tile is already that ground.) `text-mark-plate-foreground`
 * below is not a tint of a brand mark: an `<img>` ignores it entirely, and the only glyphs it reaches
 * are the two `currentColor` LUCIDE FALLBACKS (`GenericWarehouseIcon`, `GenericDestinationIcon`),
 * which are not brand marks and which would otherwise inherit a near-white `--foreground` onto a
 * fixed-white tile.
 *
 * A warehouse mark is correct HERE because this row is lineage. `ActivationStrip`'s rule 1 forbids a
 * warehouse mark on THAT strip, which is page identity — the two are not in conflict: `ActivationStrip`
 * names WHICH audience activates into WHICH destination audience (the `Users` glyph is the audience,
 * not its storage), while this plate names WHO is doing the work at each step of ONE run's pipeline,
 * and the warehouse genuinely is one of the three parties in that pipeline.
 */
import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { cn } from '@/_shared/lib/utils';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';
import { warehouseTypeMeta } from '@/settings/components/warehouseAccount/warehouseTypes';

import type { PhaseName } from '../lib/run-phases';

/**
 * OPTICAL SIZING, NOT BOX SIZING. All three marks used to be `size-4` and all three CSS boxes really
 * were 16x16 — measured with `getBoundingClientRect()` on this page, not eyeballed. What differed was
 * the INK inside those equal boxes, measured by drawing each mark to a canvas and taking the alpha
 * bounding box (the only method that works for all three, since two of them are `<img>` and one of
 * those is a raster fill, so `getBBox()` reaches neither):
 *
 *   mark               ink extent   ink area    why
 *   Postgres  size-4     11.4px      90 px^2    vendor asset, artwork inset in a 24x24 artboard
 *   Meta      size-4     12.1px      44 px^2    vendor asset, same
 *   RudderStack size-4   16.0px     199 px^2    OUR asset, a solid quarter-arc drawn edge to edge
 *
 * The two vendor marks already agreed with each other to within 6%. The outlier was ours, and the
 * reason is structural rather than per-vendor: a vendor logo is delivered on a padded artboard, our
 * mark's path fills its `viewBox` corner to corner (`getBBox()` = 15.88x16 of a 16x16 `viewBox`, ~99%).
 * So the padding a vendor asset carries inside its file, ours has to be given as SIZE — 75% of the
 * vendor box is that same ratio applied to ours, and it lands the arc's ink within ~0.5px of theirs.
 *
 * The pair was `size-4` / `size-3` (16 / 12) until the spec (Figma `500:1295`) put the glyph at 18.
 * Both moved TOGETHER, keeping the 75% ratio: `size-4.5` (18px) and `size-[0.84375rem]` (13.5px),
 * which lands our arc's ink at 13.4 against their 12.8 and 13.6 — the same relationship the 16/12
 * pair had. Scale the RATIO, never one side of it.
 *
 * The two are written differently because Tailwind v4's spacing scale stops at half steps: `size-4.5`
 * resolves, `size-3.375` compiles to NOTHING and the mark rendered at the plate's full 28px (measured
 * in the browser before this line was written — a silently dropped class is not a compile error).
 * `size-[0.84375rem]` is the sanctioned rem arbitrary value from ui/AGENTS.md's Tailwind rule, not a
 * hardcoded px. `PhaseMark.test.tsx`'s `sizeStep` reads both spellings and returns pixels.
 *
 * Deliberately NOT a per-vendor scale table. The warehouse and destination are backend strings and the
 * registries behind them resolve marks we have never seen; a lookup of hand-tuned factors would be
 * unmaintainable and silently wrong for the next asset. The one thing this file may assert is a fact
 * about ITS OWN mark, which is what it asserts. If a future vendor asset turns out to be drawn
 * full-bleed too, the fix is to pad the asset, not to grow this table.
 *
 * Both classes reach the mark through `className`, which every mark component merges LAST via `cn()`
 * (ui skill rule 16) — so `size-[0.84375rem]` beats `RudderstackBrandLogo`'s built-in `size-4` and
 * `text-mark-plate-brand` beats its `text-brand-mark` through `twMerge`. That is why this needs no new
 * prop on the shared brand component, and ui/AGENTS.md's "never recreate the mark inline" is untouched:
 * the mark still comes from `RudderstackBrandLogo`.
 */
const VENDOR_MARK_CLASS = 'size-4.5 text-mark-plate-foreground';
const RUDDERSTACK_MARK_CLASS = 'size-[0.84375rem] text-mark-plate-brand';

/**
 * AN IDLE PHASE'S MARK RECEDES. The spec puts `opacity 0.4` on the mark frame for both flavours of
 * idle — the PENDING rows of a live run (State=Extracting) and the VOID rows of a run that died
 * before reaching them (State=Failed-in-extract). Without it a phase that never ran wears its
 * vendor's mark at full strength beside a row with no number at all, which reads as a party that
 * did work rather than one that did not.
 *
 * On the FRAME, not on the mark: a brand mark is never desaturated or tinted here (see this file's
 * note above), and dimming the whole plate keeps that rule — the plate and its glyph fade together,
 * as one object stepping back. It is also NOT a substitute for the row's `sr-only` word: opacity
 * reaches no screen reader, which is exactly why `PhaseStage` keeps "Not started" / "Did not run".
 */
const IDLE_MARK_CLASS = 'opacity-40';

interface PhaseMarkProps {
  phase: PhaseName;
  /** The phase has not run — pending or void. Recedes the plate; see {@link IDLE_MARK_CLASS}. */
  idle?: boolean;
  /** `RawSqlAudience.sourceDefinitionName` — resolved case-insensitively; absent falls back generic. */
  warehouseName?: string | undefined;
  /** `destination.definitionName` — resolved through the same registry `ActivationStrip` uses. */
  destinationDefinitionName?: string | undefined;
  className?: string;
}

export function PhaseMark({
  phase,
  idle = false,
  warehouseName,
  destinationDefinitionName,
  className,
}: PhaseMarkProps): React.ReactElement {
  const Mark =
    phase === 'extract'
      ? warehouseTypeMeta(warehouseName ?? '').Icon
      : phase === 'send'
        ? destinationTypeIcon(destinationDefinitionName).Icon
        : undefined;

  return (
    <span
      // Decorative — the phase is named for assistive tech by the row's own `sr-only` label, the same
      // split ActivationStrip uses for its glyphs (see that file's header comment).
      aria-hidden
      data-slot={`phase-mark-${phase}`}
      className={cn(
        // `logo-plate-off`: this tile IS the mark's plate, so the vendor mark inside draws bare rather
        // than stacking the round dark-mode logo plate on top of it.
        'flex size-7 shrink-0 items-center justify-center rounded-lg bg-mark-plate logo-plate-off',
        idle && IDLE_MARK_CLASS,
        className,
      )}
    >
      {phase === 'plan' ? (
        <RudderstackBrandLogo variant="compact" className={RUDDERSTACK_MARK_CLASS} />
      ) : Mark ? (
        <Mark className={VENDOR_MARK_CLASS} />
      ) : null}
    </span>
  );
}
